import { ReactNode, useState, useEffect } from "react";
import Accordion from "react-bootstrap/Accordion";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { SignalModel } from "./types";
import {
    TwincatSignalRow,
    ModbusSignalRow,
    LoraSignalRow,
    EthernetSignalRow,
    OtherSignalRow,
    OtherEdgeSignalRow,
    AdvantechSignalRow,
} from "./SignalProtocolRows";
import { Button } from "react-bootstrap";
import { SettingsModal } from "./SettingsModals";
import { useTranslation } from 'react-i18next';

interface EdgeSignalsProps {
    signals: SignalModel[];
    siteGUID: string;
}



type SignalTableProps = {
    hasSparkChart?: boolean;
    columnNames: string[];
    children: ReactNode;
};

const SignalTable = ({ hasSparkChart, columnNames, children }: SignalTableProps) => {
    const { t } = useTranslation();
    return (
        <table className="w-100" style={{ tableLayout: "auto" }}>
            <thead>
                <tr>
                    <th>{t("components.signalsetup.signalaccordions.signal")}</th>
                    {hasSparkChart && <th className="d-none d-lg-block" />}
                    {columnNames.map((colName: string) => (
                        <th key={`header-${colName}`} className="text-center">{colName}</th>
                    ))}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    );
};

export const EdgeSignals = ({ signals, siteGUID }: EdgeSignalsProps) => {
    const { t } = useTranslation();
    const {
        loading,
        devices,
        signals: edgeSignals,
    } = useSelector((state: RootState) => state.edgeSignal);
    const [showSignalSettingsModal, setShowSignalSettingsModal] = useState(false);
    const [settingsModalSignal, setSettingsModalSignal] = useState<SignalModel | undefined>(undefined);
    const [settingsModalProtocolID, setSettingsModalProtocolID] = useState<number>(0)

    const EDGE_ACCORDION_DISPLAY = [
        {
            protocolID: 0,
            protocolName: "Twincat",
            hasSparkChart: true,
            columnNames: [t("components.signalsetup.signalaccordions.device"), t("components.signalsetup.signalaccordions.variablename"), t("components.signalsetup.signalaccordions.variablelocation"), t("components.signalsetup.signalaccordions.advanced")],
        },
        {
            protocolID: 1,
            protocolName: "Modbus",
            hasSparkChart: true,
            columnNames: [t("components.signalsetup.signalaccordions.device"), "Slave ID", "Address", t("components.signalsetup.signalaccordions.variablelocation"), "Aqualabo?", t("components.signalsetup.signalaccordions.advanced")],
        },
        {
            protocolID: 2,
            protocolName: "LoRa",
            hasSparkChart: true,
            columnNames: [t("components.signalsetup.signalaccordions.device"), "Device EUI", "Channel Number", t("components.signalsetup.signalaccordions.advanced")],
        },
        {
            protocolID: 4,
            protocolName: "Ethernet",
            hasSparkChart: true,
            columnNames: [t("components.signalsetup.signalaccordions.device"), "Tag", t("components.signalsetup.signalaccordions.variablename"), t("components.signalsetup.signalaccordions.advanced")],
        },
    ];

    const handleShowSettingsModal = (signal: SignalModel | undefined, protocolID: number) => {
        setSettingsModalSignal(signal);
        setSettingsModalProtocolID(protocolID);
        setShowSignalSettingsModal(true);
    };

    const createAccordionSignalRow = (protocolID: number) => {
        switch (protocolID) {
            case 0:
                return (
                    <>
                        {edgeSignals.TwincatSignals.map((item) => (
                            <TwincatSignalRow
                                key={item.Signal.SignalID + "-twincat"}
                                signal={item.Signal}
                                edgeSignal={item.EdgeSignal}
                                protocol={item.Protocol}
                                devices={devices}
                                signals={signals}
                                siteGUID={siteGUID}
                                handleShowSettingsModal={handleShowSettingsModal}
                                protocolID={protocolID}
                            />
                        ))}
                    </>
                );
            case 1:
                return (
                    <>
                        {edgeSignals.ModbusSignals.map((item) => (
                            <ModbusSignalRow
                                key={item.Signal.SignalID + "-modbus"}
                                signal={item.Signal}
                                edgeSignal={item.EdgeSignal}
                                protocol={item.Protocol}
                                signals={signals}
                                devices={devices}
                                siteGUID={siteGUID}
                                handleShowSettingsModal={handleShowSettingsModal}
                                protocolID={protocolID}
                            />
                        ))}
                    </>
                );
            case 2:
                return (
                    <>
                        {edgeSignals.LoraSignals.map((item) => (
                            <LoraSignalRow
                                key={item.Signal.SignalID + "-lora"}
                                signal={item.Signal}
                                edgeSignal={item.EdgeSignal}
                                protocol={item.Protocol}
                                signals={signals}
                                devices={devices}
                                siteGUID={siteGUID}
                                handleShowSettingsModal={handleShowSettingsModal}
                                protocolID={protocolID}
                            />
                        ))}
                    </>
                );
            case 4:
                return (
                    <>
                        {edgeSignals.EthernetSignals.map((item) => (
                            <EthernetSignalRow
                                key={item.Signal.SignalID + "-ethernet"}
                                signal={item.Signal}
                                edgeSignal={item.EdgeSignal}
                                protocol={item.Protocol}
                                signals={signals}
                                devices={devices}
                                siteGUID={siteGUID}
                                handleShowSettingsModal={handleShowSettingsModal}
                                protocolID={protocolID}
                            />
                        ))}
                    </>
                );
            default:
                return <></>;
        }
    };

    return (loading ?
        <div className="grid-item col-12 p-0">
            <div className="m-2 shadow p-0 card">
                <div className="card-header">{t("components.signalsetup.signalaccordions.fetchingsignalinfo")}</div>
                <div className="card-body p-3">
                    {t("components.signalsetup.signalaccordions.fetchingsignalinfo")}
                    <img style={{ height: "24px", width: "24px" }} src="/lib/ajax-loader.gif" />
                </div>
            </div>
        </div> :
        <Accordion defaultActiveKey="0">
            {EDGE_ACCORDION_DISPLAY.map(({ protocolID, protocolName, ...signalTableProps }) => (
                <Accordion.Item key={`protocol-${protocolName}`} eventKey={`${protocolID}`}>
                    <Accordion.Header>{`${protocolName} signals`}</Accordion.Header>
                    <Accordion.Body>
                        <SignalTable {...signalTableProps}>{createAccordionSignalRow(protocolID)}</SignalTable>
                        <Button
                            onClick={() => handleShowSettingsModal(undefined, protocolID)}
                        >
                            + {t("common.create")}
                        </Button>
                    </Accordion.Body>
                </Accordion.Item>
            ))}

            {edgeSignals.OtherSignals.length > 0 && (
                <Accordion.Item eventKey="5">
                    <Accordion.Header>{t("components.signalsetup.signalaccordions.othersignals")}</Accordion.Header>
                    <Accordion.Body>
                        <SignalTable columnNames={[t("components.signalsetup.signalaccordions.device")]}>
                            {edgeSignals.OtherSignals.map((item) => (
                                <OtherEdgeSignalRow
                                    key={item.SignalID + "-otherEdge"}
                                    signalID={item.SignalID}
                                    niceName={item.NiceName}
                                    deviceID={item.DeviceID}
                                    devices={devices}
                                />
                            ))}
                        </SignalTable>
                    </Accordion.Body>
                </Accordion.Item>)
            }

            <SettingsModal
                show={showSignalSettingsModal}
                setShow={setShowSignalSettingsModal}
                signal={settingsModalSignal}
                devices={devices}
                protocolID={settingsModalProtocolID}
                siteGUID={siteGUID}
                signals={signals}
            />
        </Accordion>
    );
};

interface AdvantechSignalsProps {
    signals: SignalModel[];
    siteGUID: string;
}
export const AdvantechSignals = ({ signals, siteGUID }: AdvantechSignalsProps) => {
    const { t } = useTranslation();
    const advSignals = useSelector((state: RootState) => state.advSignal);

    const [showSignalSettingsModal, setShowSignalSettingsModal] = useState(false);
    const [settingsModalSignal, setSettingsModalSignal] = useState<SignalModel | undefined>(undefined)

    const columnNames = [t("components.signalsetup.signalaccordions.gradient"), t("components.signalsetup.signalaccordions.intercept"), t("components.signalsetup.signalaccordions.min"), t("components.signalsetup.signalaccordions.max")];

    const handleShowSettingsModal = (signal: SignalModel | undefined) => {
        setSettingsModalSignal(signal);
        setShowSignalSettingsModal(true);
    };

    return advSignals.loading ? (
        <div className="grid-item col-12 p-0">
            <div className="m-2 shadow p-0 card">
                <div className="card-header">{t("components.signalsetup.signalaccordions.fetchingsignalinfo")}</div>
                <div className="card-body p-3">
                    {t("components.signalsetup.signalaccordions.fetchingsignalinfo")}
                    <img style={{ height: "24px", width: "24px" }} src="/lib/ajax-loader.gif" />
                </div>
            </div>
        </div>
    ) : (
        <>
            <SignalTable hasSparkChart columnNames={columnNames}>
                {advSignals.signals.map(adv =>
                    <AdvantechSignalRow
                        signal={adv.Signal}
                        advSignal={adv.Advantech}
                        signals={signals}
                        siteGUID={siteGUID}
                        handleShowSettingsModal={handleShowSettingsModal}
                    />
                )}
            </SignalTable>
        </>
    );
};

export const OtherSignals = ({ signals }: { signals: SignalModel[] }) => {
    const { t } = useTranslation();
    return (
        <table className="w-100" style={{ tableLayout: "auto", width: "100%" }}>
            <thead>
                <tr>
                    <th>{t("components.signalsetup.signalaccordions.signal")}</th>
                    <th className="d-none d-lg-block"></th>
                    <th className="text-center">{t("components.signalsetup.signalaccordions.advanced")}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {signals.map(item => (
                    <OtherSignalRow key={item.SignalID + "-othersignal"} signal={item} />
                ))}
            </tbody>
        </table>
    );
};
